import google, { mapContainerWidth, mutators, removeMapControl, repositionMapControl, verify } from "@churchofjesuschrist/maps-google";
import { classNames, css, emptyNode, proxy, proxyQueue, removeListeners, removeUndefined, styleClass } from "@churchofjesuschrist/maps-utils";
import MapsService from "@churchofjesuschrist/maps-service";
import { info, toHTMLString } from "@churchofjesuschrist/eden-icons-raw";
import LegalControlStyle from "./components/LegalControlStyle";
import Modal from "@churchofjesuschrist/maps-google-modal";
let initialized = false;
const defaultOptions = {
  //TODO?  also allow for custom data?
  style: LegalControlStyle.DEFAULT,
  disableTerms: false,
  disablePrivacy: false,
  disableCopyright: false
};
class LegalControl {
  constructor(options = {}) {
    if (!initialized) {
      verify();
      construct();
      initialized = true;
    }
    this.setOptions({
      ...defaultOptions,
      ...removeUndefined(options)
    });
  }
}
const construct = () => {
  Object.assign(defaultOptions, {
    position: google.maps.ControlPosition.BOTTOM_RIGHT
  });
  const canvasClass = styleClass("google-legal")`
        box-sizing: border-box;
        height: 14px;
        white-space: nowrap;
        overflow: hidden;
        & > * {
            display: inline-block;
            appearance: none;
            outline: none;
            border: none;
            margin: 0 0.5px;
            font-size: 10px;
            color: #000;
            padding: 0 6px;
            white-space: nowrap;
            background: rgba(245, 245, 245, 0.7);
            height: 14px;
            line-height: 14px;
            user-select: none;
            overflow: hidden;
            text-overflow: ellipsis;
            text-decoration: none;
        }
        & button {
            cursor: pointer;
        }
        & svg {
            margin: 1px 0;
            font-size: 12px;
        }
    `;
  const dialogClass = styleClass("google-legal", "dialog")`
        display: flex;
        flex-direction: column;
        align-items: start;
        & a {
            color: inherit;
            text-decoration: none;
            margin-bottom: 4px;
        }
    `;
  const legalPromise = MapsService.getLegal();
  const strings = {};
  const stringsPromise = MapsService.getStrings(["copyright", "copyright.info"]).then(results => {
    Object.assign(strings, results);
  });
  Object.setPrototypeOf(LegalControl.prototype, Object.assign(new google.maps.MVCObject(), mutators("map", "style", "disableTerms", "disablePrivacy", "disableCopyright"), (() => {
    function onAdd() {
      let map = this.getMap();
      if (this.$map !== map) {
        onRemove.call(this);
        this.$map = map;
        let queueResize = proxyQueue(updateText, this, 50);
        this.$listeners = [map.addListener("resize", queueResize)];
        if (window.ResizeObserver) {
          this.$observer = new ResizeObserver(queueResize);
          this.$observer.observe(map.getDiv());
        }
        if (!this.$div) {
          legalPromise.then(legal => {
            this.$legal = legal;
            loadLegal.call(this);
          }).catch(() => {
            console.warn("Unable to display legal.");
            onRemove.call(this);
          });
        }
        updateClassName.call(this);
        updatePosition.call(this);
      }
    }
    function onRemove() {
      if (removeListeners(this.$listeners)) {
        delete this.$listeners;
      }
      if (this.$observer) {
        this.$observer.disconnect();
        delete this.$observer;
      }
      removeMapControl(this.$map, this.$div);
      delete this.$map;
    }
    function loadLegal() {
      if (!this.$div && this.$legal) {
        this.$div = document.createElement("div");
        updateText.call(this);
        updateClassName.call(this);
        updatePosition.call(this);
      }
    }
    function getLegalElements() {
      let elements = [];
      if (this.$legal) {
        if (this.$legal.terms && !this.getDisableTerms()) {
          let terms = document.createElement("a");
          terms.href = this.$legal.terms.url;
          terms.target = "_blank";
          terms.rel = "noopener";
          terms.innerHTML = this.$legal.terms.text;
          elements.push(terms);
        }
        if (this.$legal.privacy && !this.getDisablePrivacy()) {
          let privacy = document.createElement("a");
          privacy.href = this.$legal.privacy.url;
          privacy.target = "_blank";
          privacy.rel = "noopener";
          privacy.innerHTML = this.$legal.privacy.text;
          elements.push(privacy);
        }
        if (this.$legal.copyright && !this.getDisableCopyright()) {
          let copyright = document.createElement("span");
          copyright.innerHTML = this.$legal.copyright;
          elements.push(copyright);
        }
      }
      return elements;
    }
    function getMode() {
      let mode = 1,
        style = this.getStyle(),
        map = this.getMap(),
        mapWidth = map ? mapContainerWidth(map) : null;
      switch (style) {
        case LegalControlStyle.COMPRESSED:
          // 75px
          mode = 2;
          break;
        case LegalControlStyle.EXPANDED:
          // 600px
          mode = 1;
          break;
        case LegalControlStyle.DEFAULT:
        default:
          // standard Google legal = 450px
          if (mapWidth && mapWidth <= 1050) {
            mode = 2;
          } else {
            mode = 1;
          }
          break;
      }
      if (mode === 2) {
        let position = this.get("position");
        if ((position === google.maps.ControlPosition.BOTTOM_RIGHT || position === google.maps.ControlPosition.BOTTOM_LEFT) && mapWidth && mapWidth <= 525) {
          mode = 3;
        }
      }
      return mode;
    }
    function onButtonClick() {
      stringsPromise.then(() => {
        let content = document.createElement("div");
        content.className = dialogClass;
        let elements = getLegalElements.call(this);
        elements.forEach(element => {
          content.appendChild(element);
        });
        new Modal({
          title: strings["copyright"],
          content,
          map: this.getMap()
        });
      });
    }
    function updateText() {
      if (this.$div) {
        emptyNode(this.$div);
        removeMapControl(this.$map, this.$div);
        let elements = getLegalElements.call(this);
        if (elements.length) {
          let mode = getMode.call(this);
          switch (mode) {
            case 1:
              elements.forEach(element => {
                this.$div.appendChild(element);
              });
              break;
            case 2:
              {
                let button = document.createElement("button");
                button.type = "button";
                stringsPromise.then(() => {
                  button.innerHTML = strings["copyright.info"];
                  button.title = strings["copyright"];
                });
                button.addEventListener("click", proxy(onButtonClick, this));
                this.$div.appendChild(button);
                break;
              }
            case 3:
              {
                let button = document.createElement("button");
                button.type = "button";
                button.innerHTML = toHTMLString(info);
                stringsPromise.then(() => {
                  button.title = strings["copyright"];
                });
                button.addEventListener("click", proxy(onButtonClick, this));
                this.$div.appendChild(button);
                break;
              }
          }
          updatePosition.call(this);
        }
      }
    }
    function updatePosition() {
      let position = this.get("position");
      let align = "center";
      switch (position) {
        case google.maps.ControlPosition.TOP_LEFT:
        case google.maps.ControlPosition.LEFT_TOP:
        case google.maps.ControlPosition.LEFT_CENTER:
        case google.maps.ControlPosition.LEFT_BOTTOM:
        case google.maps.ControlPosition.BOTTOM_LEFT:
          align = "left";
          break;
        case google.maps.ControlPosition.TOP_RIGHT:
        case google.maps.ControlPosition.RIGHT_TOP:
        case google.maps.ControlPosition.RIGHT_CENTER:
        case google.maps.ControlPosition.RIGHT_BOTTOM:
        case google.maps.ControlPosition.BOTTOM_RIGHT:
          align = "right";
          break;
      }
      css(this.$div, {
        textAlign: align
      });
      repositionMapControl(this.getMap(), this.$div, position);
    }
    function updateClassName() {
      if (this.$div) {
        let className = this.get("className");
        this.$div.className = classNames(className, canvasClass);
      }
    }
    return {
      getDiv: function () {
        return this.$div;
      },
      changed: function (key) {
        switch (key) {
          case "map":
            if (this.getMap()) {
              onAdd.call(this);
            } else {
              onRemove.call(this);
            }
            break;
          case "position":
            updatePosition.call(this);
            break;
          case "className":
            updateClassName.call(this);
            break;
          case "style":
          case "disableTerms":
          case "disablePrivacy":
          case "disableCopyright":
            updateText.call(this);
            break;
        }
      }
    };
  })()));
};
export default LegalControl;
export { LegalControlStyle };